import { useContext } from 'react';
import { styled } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Button from "../../../../customs/Button";
import apiCall from "../../../../utils/apiCall";
import { NotificationContext } from '../../../../providers/notification';

interface ComponentProps {
    id: number;
    specializationName: string;
    handleClose: () => void;
}

const DeleteSpecialization: React.FC<ComponentProps> = ({ id, specializationName, handleClose }) => {
    const queryClient = useQueryClient();
    const { showNotification } = useContext(NotificationContext);

    const deleteSpecialization = (id: number) => {
        return apiCall().delete(`/SetUp/DeleteSpecialization?id=${id}`);
    };

    const deleteSpecializationMutation = useMutation(['delete-specialization'], deleteSpecialization, {retry: 0});

    const deleteSpecializationHandler = async (id: number) => {
        try { 
            await deleteSpecializationMutation.mutateAsync(id, {
                onSuccess: () => {
                    showNotification({
                        message: 'Specialization deleted successfully',
                        type: 'success'
                    })
                    queryClient.refetchQueries(['get-specializations']);
                    handleClose();
                }
            })
        } catch(error: any) {
            showNotification({
                message: error?.response?.data?.title || error?.message,
                type: 'error',
            })
        }
    };

    const handleDeleteSpecialization = (id: number) => deleteSpecializationHandler(id);

    return (
        <StyledContainer>
            <StyledText>You are about to delete <strong>{specializationName}</strong></StyledText>
            <ButtonWrapper>
                <ButtonContainer>
                    <StyledButton onClick={handleClose} text='Cancel'/>
                    <StyledButton 
                        onClick={() => handleDeleteSpecialization(id)} 
                        type="submit" 
                        text={deleteSpecializationMutation.isLoading ? 'Deleting...' : 'Yes, Delete'}
                        disabled={deleteSpecializationMutation.isLoading}
                    />
                </ButtonContainer>
            </ButtonWrapper>
        </StyledContainer>
    )
}

export default DeleteSpecialization;

const StyledContainer = styled('div')`
    margin-inline: 3.5rem;
    margin-block: 2.5rem;
`

const ButtonWrapper = styled('div')`
    display: flex;
    justify-content: flex-end;
    margin-block: 2.5rem;
`

const ButtonContainer = styled('div')`
    display: flex;
    gap: 2rem;
`

const StyledButton = styled(Button)`
    padding-block: 1rem;
    padding-inline: 2rem;
    background-color: #DD5151;
`

const StyledText = styled('span')`
    color: #232323;
    font-size: 1.4rem;
    font-weight: 400;
`